import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Tag, Lock, FolderPlus, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 10 hazır renk paleti (Tailwind class isimleri)
export const PRESET_COLORS = [
  { id: 'slate', label: 'Gri', cls: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'bg-slate-500' },
  { id: 'emerald', label: 'Yeşil', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  { id: 'blue', label: 'Mavi', cls: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  { id: 'purple', label: 'Mor', cls: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  { id: 'amber', label: 'Sarı', cls: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  { id: 'pink', label: 'Pembe', cls: 'bg-pink-100 text-pink-700 border-pink-200', dot: 'bg-pink-500' },
  { id: 'rose', label: 'Gül', cls: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  { id: 'cyan', label: 'Camgöbeği', cls: 'bg-cyan-100 text-cyan-700 border-cyan-200', dot: 'bg-cyan-500' },
  { id: 'indigo', label: 'Çivit', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  { id: 'orange', label: 'Turuncu', cls: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
];

export const getColorMeta = (colorId) =>
  PRESET_COLORS.find(c => c.id === colorId) || PRESET_COLORS[0];

const CategoryRow = ({ cat, onDelete }) => {
  const colorMeta = getColorMeta(cat.color);
  return (
    <div
      className={`flex items-center justify-between p-2.5 rounded-lg border ${cat.is_default ? 'bg-slate-50 border-slate-200' : `${colorMeta.cls}`}`}
      data-testid={`category-row-${cat.id}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className={`w-3 h-3 rounded-full ${colorMeta.dot} flex-shrink-0`}></span>
        <span className="text-sm font-medium truncate">{cat.name}</span>
        {cat.is_default && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-white">
            <Lock className="w-2.5 h-2.5 mr-0.5" /> Varsayılan
          </Badge>
        )}
      </div>
      {cat.is_default ? (
        <span className="text-[10px] text-slate-400 italic">silinemez</span>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(cat)}
          className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-100 hover:text-rose-700"
          data-testid={`delete-category-${cat.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
};

const CategoryManagerModal = ({ open, onClose, onChange }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('income');
  const [name, setName] = useState('');
  const [color, setColor] = useState('slate');

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (open) fetchCategories();
  }, [open]);

  const filteredCats = categories.filter(c => c.type === type)
    .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0) || a.name.localeCompare(b.name, 'tr'));

  const handleAdd = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error('Kategori adı girin'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/categories`, { name: trimmed, type, color });
      toast.success(`"${trimmed}" eklendi`);
      setName('');
      setColor('slate');
      await fetchCategories();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kategori eklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`"${cat.name}" kategorisini silmek istediğinizden emin misiniz?\n\nBu kategoriyle eski kayıtlarınız etkilenmeyecek, sadece dropdown listesinden kaldırılacak.`)) return;
    try {
      await axios.delete(`${API}/categories/${cat.id}`);
      toast.success(`"${cat.name}" silindi`);
      await fetchCategories();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinemedi');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="category-manager-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-indigo-600" /> Kategori Yönetimi
          </DialogTitle>
          <DialogDescription>
            Gelir ve gider kategorilerinizi düzenleyin. Varsayılan kategoriler silinemez ancak yeni kategoriler ekleyebilirsiniz.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={type} onValueChange={setType}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="income" className="gap-1.5" data-testid="cat-tab-income">
              <TrendingUp className="w-4 h-4" /> Gelir Kategorileri
            </TabsTrigger>
            <TabsTrigger value="expense" className="gap-1.5" data-testid="cat-tab-expense">
              <TrendingDown className="w-4 h-4" /> Gider Kategorileri
            </TabsTrigger>
          </TabsList>

          <TabsContent value={type} className="mt-4 space-y-4">
            {/* Yeni kategori ekleme formu */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-3 space-y-3">
              <p className="text-xs font-semibold text-indigo-900 uppercase tracking-wide flex items-center gap-1.5">
                <FolderPlus className="w-3.5 h-3.5" /> Yeni {type === 'income' ? 'Gelir' : 'Gider'} Kategorisi Ekle
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Kategori Adı</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Örn: Sponsor Geliri"
                    className="h-9"
                    data-testid="new-category-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Renk</Label>
                  <div className="grid grid-cols-5 gap-1.5 mt-1">
                    {PRESET_COLORS.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setColor(c.id)}
                        className={`h-9 rounded border-2 transition-all flex items-center justify-center ${
                          color === c.id ? 'border-slate-900 ring-2 ring-slate-300 scale-105' : 'border-slate-200 hover:border-slate-400'
                        } ${c.cls}`}
                        title={c.label}
                        data-testid={`color-pick-${c.id}`}
                      >
                        <span className={`w-3 h-3 rounded-full ${c.dot}`}></span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <Button
                onClick={handleAdd}
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="add-category-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Ekle
              </Button>
            </div>

            {/* Mevcut kategoriler listesi */}
            <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                {filteredCats.length} kategori
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1" data-testid="category-list">
                {filteredCats.length === 0 ? (
                  <p className="col-span-2 text-center py-6 text-slate-400 text-sm">Henüz kategori yok</p>
                ) : filteredCats.map(cat => (
                  <CategoryRow key={cat.id} cat={cat} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <Button variant="outline" onClick={onClose} className="w-full mt-2" data-testid="close-category-modal">
          Kapat
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryManagerModal;
